import React, { useState, useEffect } from "react";
import {
  MdArrowDropUp,
  MdOutlineCalendarToday,
  MdBarChart,
} from "react-icons/md";
import Card from "components/card";
import LineChart from "components/charts/LineChart";
import { useAuth } from "../../../../context/AuthContext";

const TotalLinksOverall = () => {
  const { token, currentLang, API_URL, getCsrfToken, user } = useAuth();
  
  const [chartData, setChartData] = useState({
    series: [{
      name: "Общее количество ссылок",
      data: [],
      color: "#4318FF",
    }],
    categories: [],
    totalLinks: 0,
    growth: 0
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Генерация дат за последние 30 дней
  const generateDateRange = () => {
    const dates = [];
    const categories = [];
    const today = new Date();
    
    for (let i = 29; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      
      dates.push(date.toISOString().split('T')[0]); // YYYY-MM-DD
      categories.push(date.toLocaleDateString('ru-RU', { 
        day: '2-digit', 
        month: '2-digit' 
      }));
    }
    
    return { dates, categories };
  };

  // Загрузка данных о ссылках
  const fetchLinkStats = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }

      const { dates, categories } = generateDateRange();
      
      // Получаем все ссылки пользователя
      const response = await fetch(`${API_URL}${currentLang}/links?user_id=${user?.id || ''}&limit=1000`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': currentCsrfToken,
        },
        credentials: 'include',
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        const links = data.links || [];
        
        // Подсчитываем накопительное количество ссылок по дням
        const cumulativeLinksByDay = {};
        let runningTotal = 0;
        
        dates.forEach(date => {
          cumulativeLinksByDay[date] = runningTotal;
        });
        
        // Сортируем ссылки по дате размещения
        const sortedLinks = links
          .filter(link => link.dat)
          .sort((a, b) => new Date(a.dat) - new Date(b.dat));
        
        // Вычисляем накопительную сумму
        sortedLinks.forEach(link => {
          const linkDate = new Date(link.dat).toISOString().split('T')[0];
          if (cumulativeLinksByDay[linkDate] !== undefined) {
            // Увеличиваем счетчик для всех последующих дат
            Object.keys(cumulativeLinksByDay).forEach(date => {
              if (date >= linkDate) {
                cumulativeLinksByDay[date]++;
              }
            });
          }
        });
        
        // Формируем данные для графика
        const chartSeries = Object.values(cumulativeLinksByDay);
        const totalLinks = chartSeries[chartSeries.length - 1] || 0;
        
        // Вычисляем рост (сравниваем последние 7 дней с предыдущими 7 днями)
        const last7Days = chartSeries.slice(-7);
        const prev7Days = chartSeries.slice(-14, -7);
        
        const last7Growth = last7Days.length > 0 ? last7Days[last7Days.length - 1] - last7Days[0] : 0;
        const prev7Growth = prev7Days.length > 0 ? prev7Days[prev7Days.length - 1] - prev7Days[0] : 0;
        
        const growth = prev7Growth > 0 ? ((last7Growth - prev7Growth) / prev7Growth) * 100 : 0;
        
        setChartData({
          series: [{
            name: "Общее количество ссылок",
            data: chartSeries,
            color: "#4318FF",
          }],
          categories,
          totalLinks,
          growth
        });
      } else {
        throw new Error(data.message || 'Ошибка загрузки данных');
      }
    } catch (error) {
      console.error('Ошибка загрузки статистики ссылок:', error);
      setError('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLinkStats();
  }, []);

  const chartOptions = {
    legend: {
      show: false,
    },
    theme: {
      mode: "light",
    },
    chart: {
      type: "line",
      toolbar: {
        show: false,
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: "smooth",
      width: 3,
    },
    tooltip: {
      style: {
        fontSize: "12px",
        fontFamily: undefined,
        backgroundColor: "#000000"
      },
      theme: 'dark',
      y: {
        formatter: function(value) {
          return value + " ссылок";
        }
      }
    },
    grid: {
      show: false,
    },
    xaxis: {
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        style: {
          colors: "#A3AED0",
          fontSize: "10px",
          fontWeight: "500",
        },
        rotate: -45,
        rotateAlways: false,
      },
      type: "text",
      categories: chartData.categories,
    },
    yaxis: {
      show: false,
    },
    colors: ["#4318FF"],
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "vertical",
        shadeIntensity: 0.1,
        gradientToColors: ["#4318FF"],
        inverseColors: false,
        opacityFrom: 0.8,
        opacityTo: 0.1,
        stops: [0, 100]
      }
    }
  };

  return (
    <Card extra="!p-4 sm:!p-[20px] text-center">
      <div className="flex flex-col sm:flex-row justify-between gap-3 sm:gap-0">
        <button className="linear mt-1 flex items-center justify-center gap-2 rounded-lg bg-lightPrimary p-2 text-gray-600 transition duration-200 hover:cursor-pointer hover:bg-gray-100 active:bg-gray-200 dark:bg-navy-700 dark:hover:opacity-90 dark:active:opacity-80">
          <MdOutlineCalendarToday className="h-4 w-4 sm:h-5 sm:w-5" />
          <span className="text-xs sm:text-sm font-medium text-gray-600">Накопительная статистика</span>
        </button>
        <button 
          onClick={fetchLinkStats}
          disabled={loading}
          className="!linear z-[1] flex items-center justify-center rounded-lg bg-lightPrimary p-2 text-brand-500 !transition !duration-200 hover:bg-gray-100 active:bg-gray-200 dark:bg-navy-700 dark:text-white dark:hover:bg-white/20 dark:active:bg-white/10 disabled:opacity-50"
        >
          <MdBarChart className="h-5 w-5 sm:h-6 sm:w-6" />
        </button>
      </div>

      <div className="flex h-full w-full flex-col sm:flex-row justify-between sm:flex-wrap lg:flex-nowrap 2xl:overflow-hidden gap-4 sm:gap-0">
        <div className="flex flex-col">
          <p className="mt-4 sm:mt-[20px] text-2xl sm:text-3xl font-bold text-navy-700 dark:text-white">
            {loading ? "..." : chartData.totalLinks}
          </p>
          <div className="flex flex-col items-start">
            <p className="mt-2 text-xs sm:text-sm text-gray-600">Всего размещенных ссылок</p>
            <div className="flex flex-row items-center justify-center">
              {chartData.growth >= 0 ? (
                <MdArrowDropUp className="font-medium text-green-500 h-4 w-4 sm:h-5 sm:w-5" />
              ) : (
                <MdArrowDropUp className="font-medium text-red-500 transform rotate-180 h-4 w-4 sm:h-5 sm:w-5" />
              )}
              <p className={`text-xs sm:text-sm font-bold ${chartData.growth >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                {loading ? "..." : `${chartData.growth >= 0 ? '+' : ''}${chartData.growth.toFixed(1)}%`}
              </p>
            </div>
          </div>
        </div>
        <div className="h-48 sm:h-full w-full">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-brand-500"></div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-red-500 text-xs sm:text-sm">{error}</div>
            </div>
          ) : (
            <LineChart
              series={chartData.series}
              options={chartOptions}
            />
          )}
        </div>
      </div>
    </Card>
  );
};

export default TotalLinksOverall; 